<template>
  <!--
    DrawingCanvas
    -------------------------------------------------------------------------
    Camada SVG do desenho técnico. Renderiza grid, peças (com cotas e bordas),
    elementos técnicos, anotações e imagens. Cuida de seleção, arraste e
    clique para inserir (de acordo com a ferramenta ativa).

    Por que SVG (e não canvas/Three.js):
      - cotas, textos e linhas técnicas ficam nítidos e selecionáveis;
      - exportar para SVG/PDF no futuro é quase direto (ver StoneTechnicalDrawing2D);
      - sem dependências pesadas.
  -->
  <svg
    ref="svg"
    class="std-canvas"
    :width="width"
    :height="height"
    :viewBox="`0 0 ${width} ${height}`"
    :class="{
      'std-canvas--tool': activeTool && activeTool !== 'select',
      'std-canvas--dark': darkTheme
    }"
    :style="{ '--std-text-scale': textScale }"
    @mousedown="onCanvasMouseDown"
    @mousemove="onMouseMove"
    @mouseup="endDrag"
    @mouseleave="endDrag"
    @wheel.prevent="onWheel"
    @dblclick.prevent="onCanvasDblClick"
  >
    <!-- defs: padrão de grid + marcador de seta (reaproveitável p/ export) -->
    <defs>
      <pattern
        id="stdGrid"
        :width="gridSize"
        :height="gridSize"
        patternUnits="userSpaceOnUse"
      >
        <path
          :d="`M ${gridSize} 0 L 0 0 0 ${gridSize}`"
          fill="none"
          :stroke="darkTheme ? 'rgba(233,237,239,0.08)' : 'rgba(7,94,84,0.10)'"
          stroke-width="1"
        />
      </pattern>
      <marker
        id="stdArrow"
        markerWidth="10"
        markerHeight="10"
        refX="8"
        refY="3"
        orient="auto"
        markerUnits="strokeWidth"
      >
        <path d="M0,0 L8,3 L0,6 Z" :fill="darkTheme ? '#aebac1' : '#173b33'" />
      </marker>
      <!-- seta de COTA: usada nas duas pontas (marker-start usa o reverse) -->
      <marker
        id="stdDim"
        markerWidth="6"
        markerHeight="5"
        refX="5"
        refY="2.5"
        orient="auto-start-reverse"
        markerUnits="userSpaceOnUse"
      >
        <path d="M0,0 L5,2.5 L0,5 Z" :fill="darkTheme ? '#aebac1' : '#173b33'" />
      </marker>
      <!-- textura de tijolo da PAREDE (fiadas em amarração/"running bond") —
           usada como paint (stroke/fill) do traço da faixa da parede. -->
      <pattern
        id="stdBrickPattern"
        :width="brickTile().w"
        :height="brickTile().h"
        patternUnits="userSpaceOnUse"
      >
        <rect :width="brickTile().w" :height="brickTile().h" :fill="darkTheme ? '#8a4a34' : '#b5602f'" />
        <line
          x1="0" y1="0" :x2="brickTile().w" y2="0"
          :stroke="darkTheme ? '#2c150f' : '#f2e4d4'" stroke-width="1"
        />
        <line
          x1="0" :y1="brickTile().h / 2" :x2="brickTile().w" :y2="brickTile().h / 2"
          :stroke="darkTheme ? '#2c150f' : '#f2e4d4'" stroke-width="1"
        />
        <line
          x1="0" y1="0" x2="0" :y2="brickTile().h / 2"
          :stroke="darkTheme ? '#2c150f' : '#f2e4d4'" stroke-width="1"
        />
        <line
          :x1="brickTile().w / 2" :y1="brickTile().h / 2" :x2="brickTile().w / 2" :y2="brickTile().h"
          :stroke="darkTheme ? '#2c150f' : '#f2e4d4'" stroke-width="1"
        />
      </pattern>
    </defs>

    <!-- fundo + grid -->
    <rect :width="width" :height="height" :fill="darkTheme ? '#0b141a' : '#eef3f0'" />
    <rect
      v-if="showGrid"
      :width="width"
      :height="height"
      fill="url(#stdGrid)"
    />

    <!-- grupo de conteúdo: zoom/pan aplicados aqui (não afeta dados) -->
    <g ref="content" :transform="contentTransform">
      <!-- IMAGENS (camada de baixo) -->
      <g
        v-for="img in images"
        :key="img.id"
        :transform="`rotate(${img.rotation || 0} ${img.x + img.width / 2} ${img.y + img.height / 2})`"
      >
        <image
          :href="img.src"
          :xlink:href="img.src"
          :x="img.x"
          :y="img.y"
          :width="img.width"
          :height="img.height"
          :opacity="img.opacity"
          preserveAspectRatio="xMidYMid slice"
          class="std-image"
          @mousedown.stop="startDrag($event, 'image', img.id)"
        />
        <rect
          :x="img.x"
          :y="img.y"
          :width="img.width"
          :height="img.height"
          fill="none"
          :stroke="isSelected('image', img.id) ? '#173b33' : 'rgba(23,59,51,0.35)'"
          :stroke-width="isSelected('image', img.id) ? 1.8 : 1"
          stroke-dasharray="4 3"
          pointer-events="none"
        />
        <!-- handle de resize (canto inferior direito) -->
        <rect
          v-if="isSelected('image', img.id) && !readonly && !img.locked"
          :x="img.x + img.width - 6 * textScale"
          :y="img.y + img.height - 6 * textScale"
          :width="12 * textScale"
          :height="12 * textScale"
          fill="#ffffff"
          stroke="#173b33"
          :stroke-width="1.4 * textScale"
          class="std-handle"
          @mousedown.stop="startResize($event, 'image', img.id)"
        />
      </g>

      <!-- PEÇAS de pedra (retângulos em escala; rotation gira em torno do
           próprio centro — usado por saias laterais e pelo "Girar 90°") -->
      <g
        v-for="dp in decoratedPieces"
        :key="dp.piece.id"
        class="std-piece"
        :transform="dp.piece.rotation ? `rotate(${dp.piece.rotation} ${dp.bounds.cx} ${dp.bounds.cy})` : ''"
        @mousedown.stop="startDrag($event, 'piece', dp.piece.id)"
        @contextmenu.prevent.stop="emitPieceMenu(dp, $event)"
        @dragenter.prevent
        @dragover.prevent
        @drop.prevent.stop="onPieceDrop(dp, $event)"
      >
        <!-- corpo da peça: silhueta (L/U/borracha) > cantos arredondados
             unilaterais > retângulo de cantos RETOS (padrão) -->
        <polygon
          v-if="dp.outline"
          :points="dp.outline.polygon"
          :fill="dp.fill"
          :stroke="pieceStroke(dp)"
          :stroke-width="1.4"
        />
        <path
          v-else-if="dp.cornerPath"
          :d="dp.cornerPath"
          :fill="dp.fill"
          :stroke="pieceStroke(dp)"
          :stroke-width="1.4"
        />
        <rect
          v-else
          :x="dp.bounds.x"
          :y="dp.bounds.y"
          :width="dp.bounds.w"
          :height="dp.bounds.h"
          :fill="dp.fill"
          :stroke="pieceStroke(dp)"
          :stroke-width="1.4"
        />

        <!-- DESTAQUE de seleção: contorno tracejado externo (UI, não imprime
             — em readonly nada fica selecionado) -->
        <rect
          v-if="false && isSelected('piece', dp.piece.id) && !readonly"
          :x="dp.bounds.x - 5"
          :y="dp.bounds.y - 5"
          :width="dp.bounds.w + 10"
          :height="dp.bounds.h + 10"
          fill="none"
          stroke="#128c7e"
          :stroke-width="1.4 * textScale"
          :stroke-dasharray="`${6 * textScale} ${4 * textScale}`"
          pointer-events="none"
        />

        <!-- bordas com acabamento (sobrepõem o contorno; L/U = vários segmentos) -->
        <template v-for="edge in dp.activeEdges">
          <line
            v-for="(seg, sgi) in edge.lines"
            :key="`${dp.piece.id}-${edge.edge}-${sgi}`"
            :x1="seg.x1"
            :y1="seg.y1"
            :x2="seg.x2"
            :y2="seg.y2"
            :stroke="edge.style.stroke"
            :stroke-width="edge.style.strokeWidth"
            :stroke-dasharray="edge.style.dash || ''"
            stroke-linecap="round"
          />
        </template>
        <!-- perfil do acabamento (linha dupla, tracinhos, ticks 45°...) -->
        <template v-for="edge in dp.activeEdges">
          <line
            v-for="(decor, di) in edge.decorations"
            :key="`${dp.piece.id}-${edge.edge}-decor-${di}`"
            class="std-edge-decor"
            :x1="decor.x1"
            :y1="decor.y1"
            :x2="decor.x2"
            :y2="decor.y2"
            :stroke="edge.style.stroke"
            :stroke-width="decor.strokeWidth"
            :stroke-dasharray="decor.dash || ''"
            :stroke-linecap="decor.linecap || 'butt'"
          />
        </template>
        <!-- área de clique das bordas: um clique alterna o acabamento;
             clique direito abre o menu de tipos -->
        <template v-if="!readonly && activeTool === 'select' && dp.piece.isStone">
          <template v-for="edge in dp.edges">
            <line
              v-for="(seg, sgi) in edge.lines"
              :key="`hit-${dp.piece.id}-${edge.edge}-${sgi}`"
              class="std-edge-hit"
              :x1="seg.x1"
              :y1="seg.y1"
              :x2="seg.x2"
              :y2="seg.y2"
              stroke="transparent"
              stroke-width="28"
              @mousedown.stop
              @click.stop="emitEdgeClick(dp, edge, $event)"
              @contextmenu.prevent.stop="emitEdgeMenu(dp, edge, $event)"
              @dragenter.prevent.stop
              @dragover.prevent.stop
              @drop.prevent.stop="onEdgeDrop(dp, edge, $event)"
            />
          </template>
        </template>

        <!-- SUGESTÃO-FANTASMA de peça secundária (saia/rodapé) na borda -->
        <g
          v-for="ghost in ghostStrips(dp)"
          :key="`ghost-${dp.piece.id}-${ghost.edge}`"
          class="std-ghost"
          @mousedown.stop
          @dragenter.prevent.stop
          @dragover.prevent.stop
          @drop.prevent.stop="onEdgeDrop(dp, { edge: ghost.edge }, $event)"
          @click.stop="$emit('accept-secondary', {
            pieceId: dp.piece.id,
            edge: ghost.edge,
            clientX: $event.clientX,
            clientY: $event.clientY
          })"
        >
          <rect
            :x="ghost.x"
            :y="ghost.y"
            :width="ghost.w"
            :height="ghost.h"
            rx="2"
            class="std-ghost-suggestion"
          />
          <text
            :x="ghost.labelX"
            :y="ghost.labelY"
            text-anchor="middle"
            class="std-ghost-suggestion-label" :style="fontSizes.ghostLabel"
            :transform="ghost.rotate ? `rotate(${ghost.rotate} ${ghost.labelX} ${ghost.labelY})` : ''"
          >{{ ghost.label }}</text>
        </g>

        <!-- rótulo da peça (P1 - Bancada / 2.40m x 0.60m / M²).
             Peças BAIXAS (saia etc.): vira UMA linha; muito baixas, o texto
             sai para FORA da peça (lado oposto ao da cota) — sem texto em
             cima das linhas da peça. -->
        <template v-if="pieceLabelLayout(dp) === 'stacked'">
          <text
            :x="dp.bounds.cx"
            :y="dp.bounds.cy - 4 * textScale"
            text-anchor="middle"
            class="std-piece-label" :style="fontSizes.pieceLabel"
            fill="#173b33"
            :transform="labelTextTransform(dp, dp.bounds.cx, dp.bounds.cy)"
          >{{ dp.label }}</text>
          <text
            :x="dp.bounds.cx"
            :y="dp.bounds.cy + 10 * textScale"
            text-anchor="middle"
            class="std-piece-sub" :style="fontSizes.pieceSub"
            fill="#445248"
            :transform="labelTextTransform(dp, dp.bounds.cx, dp.bounds.cy)"
          >{{ dp.sub }}</text>
        </template>
        <text
          v-else-if="pieceLabelLayout(dp) === 'inline'"
          :x="dp.bounds.cx"
          :y="dp.bounds.cy + 3 * textScale"
          text-anchor="middle"
          class="std-piece-sub" :style="fontSizes.pieceSub"
          fill="#173b33"
          :transform="labelTextTransform(dp, dp.bounds.cx, dp.bounds.cy)"
        >{{ dp.label }} · {{ dp.sub }}</text>
        <text
          v-else
          :x="dp.bounds.cx"
          :y="dp.dimSides.lengthSide === 'top' ? dp.bounds.y + dp.bounds.h + 12 * textScale : dp.bounds.y - 5 * textScale"
          text-anchor="middle"
          class="std-piece-sub" :style="fontSizes.pieceSub"
          fill="#173b33"
          :transform="labelTextTransform(dp, dp.bounds.cx, dp.dimSides.lengthSide === 'top' ? dp.bounds.y + dp.bounds.h + 12 * textScale : dp.bounds.y - 5 * textScale)"
        >{{ dp.label }} · {{ dp.sub }}</text>

        <!-- COTAS técnicas (chamada + linha com setas nas duas pontas).
             Duplo clique na cota abre a edição inline da medida. -->
        <g v-if="showMeasurements" class="std-dim">
          <template v-for="(dim, dk) in dp.dims">
            <line
              v-for="(e, ei) in dim.extLines"
              :key="`ext-${dp.piece.id}-${dk}-${ei}`"
              v-bind="e"
              class="std-ext"
            />
            <line
              :key="`dim-${dp.piece.id}-${dk}`"
              v-bind="dim.dimLine"
              class="std-dimline"
              marker-start="url(#stdDim)"
              marker-end="url(#stdDim)"
            />
            <rect
              :key="`dbg-${dp.piece.id}-${dk}`"
              :x="dim.label.x - dimTextWidth(dim.label.text) / 2"
              :y="dim.label.y - 9 * textScale"
              :width="dimTextWidth(dim.label.text)"
              :height="11 * textScale"
              class="std-dim-bg"
              :class="{ 'std-dim-editable': !readonly && dp.piece.isStone }"
              :transform="dim.label.rotate ? `rotate(${dim.label.rotate} ${dim.label.x} ${dim.label.y})` : ''"
              @dblclick.stop="emitEditDimension(dp, dk, dim)"
              @mousedown.stop
            />
            <text
              :key="`dtx-${dp.piece.id}-${dk}`"
              :x="dim.label.x"
              :y="dim.label.y"
              text-anchor="middle"
              class="std-dim-text" :style="fontSizes.dim"
              :class="{ 'std-dim-editable': !readonly && dp.piece.isStone }"
              :transform="dim.label.rotate ? `rotate(${dim.label.rotate} ${dim.label.x} ${dim.label.y})` : ''"
              @dblclick.stop="emitEditDimension(dp, dk, dim)"
              @mousedown.stop
            >{{ dim.label.text }}</text>
          </template>
        </g>

        <!-- SWATCH do acabamento cadastrado (imagem do insumo junto à borda).
             Renderizado DEPOIS das cotas (fica por cima); a posição já foi
             desviada lateralmente por edgeSwatchItems() quando colide com o
             texto da medida — o texto da medida nunca é encoberto.
             Vive em unidades do DESENHO (cresce com o zoom, como a peça);
             REDIMENSIONÁVEL pela alça do canto com a peça selecionada. -->
        <g
          v-for="swatch in edgeSwatchItems(dp)"
          :key="`sw-${dp.piece.id}-${swatch.edge}`"
          class="std-edge-swatch"
          @mousedown.stop="startSwatchMove($event, dp.piece.id, swatch.edge, dp.piece.rotation)"
          @click.stop="emitSwatchSelect(dp)"
          @dblclick.stop="emitEdgeMenu(dp, { edge: swatch.edge }, $event)"
          @contextmenu.prevent.stop="emitEdgeMenu(dp, { edge: swatch.edge }, $event)"
        >
          <rect
            :x="swatch.x - swatch.half - 1"
            :y="swatch.y - swatch.half - 1"
            :width="swatch.size + 2"
            :height="swatch.size + 2"
            rx="2"
            class="std-edge-swatch-frame"
          />
          <image
            v-if="swatch.href"
            :href="swatch.href"
            :xlink:href="swatch.href"
            :x="swatch.x - swatch.half"
            :y="swatch.y - swatch.half"
            :width="swatch.size"
            :height="swatch.size"
            preserveAspectRatio="xMidYMid slice"
          />
          <text
            :x="swatch.labelX"
            :y="swatch.labelY"
            :text-anchor="swatch.anchor"
            class="std-edge-swatch-name" :style="fontSizes.swatchName"
            :transform="swatch.labelRotation ? `rotate(${swatch.labelRotation} ${swatch.labelX} ${swatch.labelY})` : ''"
          >{{ swatch.nome }}</text>
          <rect
            v-if="!readonly && isSelected('piece', dp.piece.id)"
            :x="swatch.x + swatch.half - 4 * textScale"
            :y="swatch.y + swatch.half - 4 * textScale"
            :width="10 * textScale"
            :height="10 * textScale"
            rx="2"
            fill="#ffffff"
            stroke="#173b33"
            :stroke-width="1.2 * textScale"
            class="std-handle"
            @mousedown.stop="startSwatchResize($event, dp.piece.id, swatch.edge)"
            @click.stop
          />
        </g>

        <!-- RECORTES VINCULADOS (cuba/cooktop/furos) -->
        <g
          v-for="cr in dp.cutouts"
          :key="`cut-${dp.piece.id}-${cr.cutout.id}`"
          class="std-cutout"
          @mousedown.stop="startDrag($event, 'cutout', cr.cutout.id, dp.piece.rotation || 0)"
          @contextmenu.prevent.stop="emitCutoutMenu(cr, $event)"
        >
          <rect
            :x="cr.hit.x"
            :y="cr.hit.y"
            :width="cr.hit.w"
            :height="cr.hit.h"
            rx="5"
            fill="transparent"
            class="std-cutout-hit"
          />
          <!-- símbolo paramétrico -->
          <template v-for="(prim, pi) in cr.primitives">
            <rect
              v-if="prim.type === 'rect'"
              :key="`cp-${cr.cutout.id}-${pi}`"
              :class="prim.class"
              :x="prim.x"
              :y="prim.y"
              :width="prim.width"
              :height="prim.height"
              :rx="prim.rx || 0"
              :stroke-dasharray="prim.dash || ''"
            />
            <circle
              v-else-if="prim.type === 'circle'"
              :key="`cp-${cr.cutout.id}-${pi}`"
              :class="prim.class"
              :cx="prim.cx"
              :cy="prim.cy"
              :r="prim.r"
            />
            <line
              v-else-if="prim.type === 'line'"
              :key="`cp-${cr.cutout.id}-${pi}`"
              :class="prim.class"
              :x1="prim.x1"
              :y1="prim.y1"
              :x2="prim.x2"
              :y2="prim.y2"
              :stroke-dasharray="prim.dash || ''"
            />
          </template>
          <!-- eixos traço-ponto -->
          <line v-bind="cr.center.h" :stroke-dasharray="cr.center.dash" class="std-centerline" />
          <line v-bind="cr.center.v" :stroke-dasharray="cr.center.dash" class="std-centerline" />
          <!-- rótulo + medida (offsets acompanham a escala do texto) -->
          <text
            :x="cr.px.cx"
            :y="cr.px.cy - cr.px.h / 2 - 4 * textScale"
            text-anchor="middle"
            class="std-cutout-label" :style="fontSizes.cutoutLabel"
          >{{ cr.cutout.label }}</text>
          <text
            :x="cr.px.cx"
            :y="cr.px.cy + cr.px.h / 2 + 10 * textScale"
            text-anchor="middle"
            class="std-cutout-label" :style="fontSizes.cutoutLabel"
          >{{ cr.sizeLabel }}</text>
          <!-- destaque de seleção + alça de redimensionamento -->
          <template v-if="isSelected('cutout', cr.cutout.id)">
            <rect
              :x="cr.hit.x"
              :y="cr.hit.y"
              :width="cr.hit.w"
              :height="cr.hit.h"
              fill="none"
              stroke="#173b33"
              stroke-width="1.2"
              stroke-dasharray="4 3"
              pointer-events="none"
            />
            <rect
              v-if="!readonly"
              :x="cr.hit.x + cr.hit.w - 7 * textScale"
              :y="cr.hit.y + cr.hit.h - 7 * textScale"
              :width="14 * textScale"
              :height="14 * textScale"
              rx="3"
              fill="#ffffff"
              stroke="#173b33"
              :stroke-width="1.4 * textScale"
              class="std-handle"
              @mousedown.stop="startCutoutResize($event, cr.cutout.id, dp.piece.rotation || 0)"
            />
          </template>
        </g>

        <!-- CHAMADAS de borda (ex.: RODA BASE EM PEDRA na frente) -->
        <text
          v-for="lbl in edgeLabelItems(dp)"
          :key="`elb-${dp.piece.id}-${lbl.edge}`"
          :x="lbl.x"
          :y="lbl.y"
          text-anchor="middle"
          class="std-cutout-label" :style="fontSizes.cutoutLabel"
          :transform="lbl.rotate ? `rotate(${lbl.rotate} ${lbl.x} ${lbl.y})` : ''"
        >{{ lbl.text }}</text>

        <!-- COTAS dos recortes/silhueta: eixo -> bordas + encadeada + L/U -->
        <g v-if="showMeasurements && (dp.cutouts.length || dp.shapeDims.length)" class="std-dim">
          <template v-for="(dim, ci) in cutoutDims(dp)">
            <line
              v-for="(e, ei) in dim.extLines"
              :key="`cext-${dp.piece.id}-${ci}-${ei}`"
              v-bind="e"
              class="std-ext"
            />
            <line
              :key="`cdim-${dp.piece.id}-${ci}`"
              v-bind="dim.dimLine"
              class="std-dimline"
              marker-start="url(#stdDim)"
              marker-end="url(#stdDim)"
            />
            <rect
              :key="`cdbg-${dp.piece.id}-${ci}`"
              :x="dim.label.x - dimTextWidth(dim.label.text) / 2"
              :y="dim.label.y - 9 * textScale"
              :width="dimTextWidth(dim.label.text)"
              :height="11 * textScale"
              class="std-dim-bg"
              :transform="dim.label.rotate ? `rotate(${dim.label.rotate} ${dim.label.x} ${dim.label.y})` : ''"
            />
            <text
              :key="`cdtx-${dp.piece.id}-${ci}`"
              :x="dim.label.x"
              :y="dim.label.y"
              text-anchor="middle"
              class="std-dim-text" :style="fontSizes.dim"
              :transform="dim.label.rotate ? `rotate(${dim.label.rotate} ${dim.label.x} ${dim.label.y})` : ''"
            >{{ dim.label.text }}</text>
          </template>
        </g>

        <!-- ALÇAS de redimensionamento (peça selecionada, ferramenta select) -->
        <template v-if="canResizePiece(dp)">
          <!-- alças com tamanho de TELA constante (contra-escala do zoom) -->
          <rect
            v-for="h in resizeHandles(dp.bounds, dp.piece.rotation)"
            :key="`rh-${dp.piece.id}-${h.handle}`"
            :x="h.x - 4 * textScale"
            :y="h.y - 4 * textScale"
            :width="8 * textScale"
            :height="8 * textScale"
            fill="#ffffff"
            stroke="#173b33"
            :stroke-width="1.4 * textScale"
            :style="{ cursor: h.cursor }"
            @mousedown.stop="startPieceResize($event, dp.piece.id, h.handle)"
          />
          <!-- alça do vértice interno da silhueta L/U (ajusta o recorte) -->
          <circle
            v-if="shapeHandlePoint(dp)"
            :cx="shapeHandlePoint(dp).x"
            :cy="shapeHandlePoint(dp).y"
            :r="6 * textScale"
            fill="#ffffff"
            stroke="#173b33"
            :stroke-width="1.6 * textScale"
            style="cursor: nwse-resize"
            @mousedown.stop="startShapeResize($event, dp.piece.id)"
          />
        </template>
      </g>

      <!-- retângulo da BORRACHA durante o arraste -->
      <rect
        v-if="eraserRect"
        :x="eraserRect.x"
        :y="eraserRect.y"
        :width="eraserRect.w"
        :height="eraserRect.h"
        class="std-eraser-rect"
        pointer-events="none"
      />

      <!-- GHOST do redimensionamento (preview + cota fantasma em tempo real);
           acompanha a rotação da peça -->
      <g
        v-if="resizeGhost"
        pointer-events="none"
        :transform="drag && drag.rotation ? `rotate(${drag.rotation} ${drag.origBounds.x + drag.origBounds.w / 2} ${drag.origBounds.y + drag.origBounds.h / 2})` : ''"
      >
        <rect
          :x="resizeGhost.x"
          :y="resizeGhost.y"
          :width="resizeGhost.w"
          :height="resizeGhost.h"
          fill="rgba(0, 0, 0, 0.03)"
          stroke="#555555"
          stroke-width="1.4"
          stroke-dasharray="6 4"
        />
        <text
          :x="resizeGhost.x + resizeGhost.w / 2"
          :y="resizeGhost.y + resizeGhost.h + 15"
          text-anchor="middle"
          class="std-ghost-dim" :style="fontSizes.ghostDim"
        >{{ resizeGhostLabels.w }}</text>
        <text
          :x="resizeGhost.x + resizeGhost.w + 8"
          :y="resizeGhost.y + resizeGhost.h / 2"
          class="std-ghost-dim" :style="fontSizes.ghostDim"
        >{{ resizeGhostLabels.h }}</text>
      </g>

      <!-- ELEMENTOS técnicos -->
      <g
        v-for="el in elements"
        :key="el.id"
        class="std-element"
        @mousedown.stop="startDrag($event, 'element', el.id)"
        @dblclick.stop="$emit('select', { kind: 'element', id: el.id })"
        @contextmenu.prevent.stop="$emit('select', { kind: 'element', id: el.id })"
      >
        <!-- retângulos (recortes, cuba, cooktop, frontão, saia, etc.) -->
        <rect
          v-if="el.shape === 'rect'"
          :x="el.x"
          :y="el.y"
          :width="el.width"
          :height="el.height"
          fill="rgba(0,0,0,0.03)"
          stroke="#173b33"
          :stroke-width="isSelected('element', el.id) ? 2.2 : 1.2"
          stroke-dasharray="5 3"
        />
        <!-- círculos (furo, recorte circular) com eixo + cota Ø -->
        <template v-else-if="el.shape === 'circle'">
          <circle
            :cx="circleDecor(el).cx"
            :cy="circleDecor(el).cy"
            :r="circleDecor(el).r"
            fill="none"
            stroke="#173b33"
            :stroke-width="isSelected('element', el.id) ? 2.2 : 1.2"
          />
          <!-- linha de eixo (traço-ponto) -->
          <line
            v-bind="circleDecor(el).center.h"
            :stroke-dasharray="circleDecor(el).center.dash"
            class="std-centerline"
          />
          <line
            v-bind="circleDecor(el).center.v"
            :stroke-dasharray="circleDecor(el).center.dash"
            class="std-centerline"
          />
          <!-- cota de diâmetro Ø -->
          <template v-if="showMeasurements">
            <line
              v-bind="circleDecor(el).dia.leader"
              class="std-dimline"
              marker-start="url(#stdDim)"
            />
            <text
              :x="circleDecor(el).dia.label.x"
              :y="circleDecor(el).dia.label.y"
              class="std-dim-text" :style="fontSizes.dim"
            >{{ circleDecor(el).dia.label.text }}</text>
          </template>
        </template>
        <!-- linha / seta / emenda / parede antiga (reta) -->
        <line
          v-else-if="el.shape === 'line' || el.shape === 'arrow'"
          :x1="el.x"
          :y1="el.y"
          :x2="el.x + el.width"
          :y2="el.y + el.height"
          stroke="#173b33"
          :stroke-width="isSelected('element', el.id) ? 2.4 : 1.4"
          :marker-end="el.shape === 'arrow' ? 'url(#stdArrow)' : ''"
        />
        <!-- PAREDE (utility:wall): referência visual — NÃO entra no
             orçamento. Faixa com espessura real de alvenaria, textura de
             tijolo (padrão SVG repetido) + ângulo de cada quina sempre
             visível (não depende de seleção). -->
        <template v-else-if="el.shape === 'bezier'">
          <!-- faixa: 2 traços empilhados no MESMO path com stroke-linejoin
               "miter" (nativo do SVG) — a quina sai em bico reto, sem
               geometria de offset manual arredondando/entortando o canto.
               O preenchimento usa o padrão de tijolo (`stdBrickPattern`)
               como "stroke" — funciona porque um traço largo É a faixa. -->
          <path
            :d="wallPathD(el)"
            fill="none"
            class="std-wall-ribbon-outline"
            :stroke="wallOutlineColor(el)"
            :style="{ strokeWidth: wallThicknessPx() + 'px' }"
          />
          <path
            :d="wallPathD(el)"
            fill="none"
            class="std-wall-ribbon-fill"
            :style="{ strokeWidth: Math.max(1, wallThicknessPx() - 3) + 'px' }"
          />
          <g v-for="ang in wallAngles(el)" :key="`wall-angle-${el.id}-${ang.index}`">
            <rect
              class="std-wall-angle-bg"
              :x="ang.labelX - dimTextWidth(Math.round(ang.deg) + '°') / 2"
              :y="ang.labelY - 9 * textScale"
              :width="dimTextWidth(Math.round(ang.deg) + '°')"
              :height="11 * textScale"
            />
            <text
              class="std-wall-angle-text" :style="fontSizes.dim"
              text-anchor="middle"
              :x="ang.labelX" :y="ang.labelY"
            >{{ Math.round(ang.deg) }}°</text>
          </g>
          <template v-if="isSelected('element', el.id) && !readonly">
            <circle
              v-for="anchor in wallHandles(el).anchors"
              :key="`wall-anchor-${el.id}-${anchor.index}`"
              :cx="anchor.x"
              :cy="anchor.y"
              :r="5 * textScale"
              class="std-wall-anchor"
              @mousedown.stop="startWallPointDrag($event, el.id, anchor.index)"
            />
            <rect
              v-for="bow in wallHandles(el).bowHandles"
              :key="`wall-bow-${el.id}-${bow.index}`"
              :x="bow.x - 4 * textScale"
              :y="bow.y - 4 * textScale"
              :width="8 * textScale"
              :height="8 * textScale"
              class="std-wall-bow"
              @mousedown.stop="startWallBowDrag($event, el.id, bow.index)"
            />
          </template>
        </template>
        <!-- texto (observação livre) -->
        <text
          v-else-if="el.shape === 'text'"
          :x="el.x"
          :y="el.y"
          class="std-element-text" :style="fontSizes.elementText"
          :fill="darkTheme ? '#e9edef' : '#173b33'"
          :font-weight="isSelected('element', el.id) ? 700 : 400"
        >{{ el.label }}</text>

        <!-- etiqueta do elemento (a parede já se identifica pelo traço) -->
        <text
          v-if="el.shape !== 'text' && el.shape !== 'bezier'"
          :x="el.x"
          :y="el.y - 4"
          class="std-element-tag" :style="fontSizes.elementTag"
        >{{ el.label }}</text>
      </g>

      <!-- rascunho da PAREDE em andamento (pontos já colocados + rubber-band
           até o cursor). Puramente visual, nada persiste até finalizar.
           Já mostra a faixa/tijolos e o ângulo da quina em tempo real,
           inclusive antes do próximo clique confirmar o ponto. -->
      <g v-if="wallDraft" pointer-events="none">
        <path
          v-if="wallDraftPreviewPoints().length > 1"
          :d="wallPathD({ points: wallDraftPreviewPoints(), bows: [] })"
          fill="none"
          class="std-wall-ribbon-outline std-wall-ribbon-outline--draft"
          :style="{ strokeWidth: wallThicknessPx() + 'px' }"
        />
        <path
          v-if="wallDraftPreviewPoints().length > 1"
          :d="wallPathD({ points: wallDraftPreviewPoints(), bows: [] })"
          fill="none"
          class="std-wall-ribbon-fill std-wall-ribbon-fill--draft"
          :style="{ strokeWidth: Math.max(1, wallThicknessPx() - 3) + 'px' }"
        />
        <g v-for="ang in wallDraftAngles()" :key="`wall-draft-angle-${ang.index}`">
          <rect
            class="std-wall-angle-bg"
            :x="ang.labelX - dimTextWidth(Math.round(ang.deg) + '°') / 2"
            :y="ang.labelY - 9 * textScale"
            :width="dimTextWidth(Math.round(ang.deg) + '°')"
            :height="11 * textScale"
          />
          <text
            class="std-wall-angle-text" :style="fontSizes.dim"
            text-anchor="middle"
            :x="ang.labelX" :y="ang.labelY"
          >{{ Math.round(ang.deg) }}°</text>
        </g>
        <circle
          v-for="(p, pi) in wallDraft.points"
          :key="`wall-draft-${pi}`"
          :cx="p.x"
          :cy="p.y"
          :r="4 * textScale"
          class="std-wall-anchor"
        />
      </g>

      <!-- ANOTAÇÕES de texto -->
      <g
        v-for="ann in annotations"
        :key="ann.id"
        class="std-annotation"
        @mousedown.stop="startDrag($event, 'annotation', ann.id)"
        @dblclick.stop="$emit('edit-annotation', ann)"
        @contextmenu.prevent.stop="$emit('annotation-menu', { id: ann.id, clientX: $event.clientX, clientY: $event.clientY })"
      >
        <!-- linha de chamada (líder) pontilhada + ponto na extremidade -->
        <template v-if="ann.leader">
          <line
            :x1="ann.x + annotationWidth(ann) / 2 - 4"
            :y1="ann.y + 4"
            :x2="ann.leader.x"
            :y2="ann.leader.y"
            class="std-leader-line"
          />
          <circle :cx="ann.leader.x" :cy="ann.leader.y" :r="2.4 * textScale" fill="#5a6660" />
          <!-- alça arrastável da ponta do líder -->
          <circle
            v-if="isSelected('annotation', ann.id) && !readonly"
            :cx="ann.leader.x"
            :cy="ann.leader.y"
            :r="7 * textScale"
            fill="rgba(90, 102, 96, 0.18)"
            stroke="#5a6660"
            class="std-leader-handle"
            @mousedown.stop="startDrag($event, 'leader', ann.id)"
          />
        </template>
        <!-- texto simples: fundo branco discreto, borda 1px, raio 2 -->
        <g :transform="`rotate(${ann.rotation || 0} ${ann.x} ${ann.y})`">
        <rect
          :x="ann.x - 4"
          :y="ann.y - (ann.fontSize || 12) * textScale"
          :width="annotationWidth(ann)"
          :height="((ann.fontSize || 12) + 8) * textScale"
          rx="2"
          :fill="darkTheme ? 'rgba(17,27,33,0.94)' : 'rgba(255,255,255,0.92)'"
          :stroke="isSelected('annotation', ann.id) ? (darkTheme ? '#25d366' : '#173b33') : (darkTheme ? 'rgba(233,237,239,0.32)' : 'rgba(23,59,51,0.28)')"
          stroke-width="1"
        />
        <text
          :x="ann.x"
          :y="ann.y"
          :font-size="(ann.fontSize || 12) * textScale"
          class="std-annotation-text"
          :fill="darkTheme ? '#e9edef' : '#37413d'"
        >{{ ann.text }}</text>
        </g>
      </g>
    </g>
  </svg>
</template>

<script>
import {
  getPieceBounds,
  getDimensionLines,
  getCenterLines,
  getDiameterCallout,
  getPieceOutline,
  getShapeDimensions,
  getPieceCornerPath,
  formatMeasure,
  globalDeltaToPieceLocal
} from "./engine/drawing/measurementUtils";
import { renderAllEdges } from "./engine/drawing/edgeFinishRenderer";
import { getRenderablePieces } from "./engine/drawing/drawingFactory";
import { installStdStyles } from "./engine/print/stdCanvasStyles";
import { pxToMeters, metersToPx } from "./engine/drawing/scaleUtils";
import {
  cutoutToPx,
  getCutoutSizeLabel,
  getCutoutAxisDimensions,
  getChainDimensions
} from "./engine/drawing/cutoutUtils";
import { getCutoutSymbol } from "./engine/drawing/symbolLibrary";
import {
  getWallPathD,
  getWallHandles,
  computeWallAngles,
  WALL_THICKNESS_M
} from "./engine/drawing/wallUtils";

// medida mínima de uma peça (2cm) e snap de 1cm ao redimensionar
const MIN_PIECE_METERS = 0.02;
const snapMeters = value => Math.round((Number(value) || 0) * 1000) / 1000;

// PRETO E BRANCO: o desenho técnico é line-art — sem cores duras.
// Papéis visuais se diferenciam por tons de cinza sutis.
const ROLE_FILL = {
  main_piece: "#ffffff",
  secondary_piece: "#f5f5f5",
  finishing: "#fafafa",
  generic: "#f7f7f7"
};
const DARK_ROLE_FILL = {
  main_piece: "#26343c",
  secondary_piece: "#202c33",
  finishing: "#1b272e",
  generic: "#202c33"
};

export default {
  name: "DrawingCanvas",
  props: {
    pieces: { type: Array, default: () => [] },
    elements: { type: Array, default: () => [] },
    annotations: { type: Array, default: () => [] },
    images: { type: Array, default: () => [] },
    width: { type: Number, default: 760 },
    height: { type: Number, default: 360 },
    scale: { type: Number, default: 1 },
    zoom: { type: Number, default: 1 },
    panX: { type: Number, default: 0 },
    panY: { type: Number, default: 0 },
    gridSize: { type: Number, default: 24 },
    showGrid: { type: Boolean, default: true },
    showMeasurements: { type: Boolean, default: true },
    selection: { type: Object, default: () => ({ kind: null, id: null }) },
    activeTool: { type: String, default: "select" },
    readonly: { type: Boolean, default: false },
    // modo "line-art": peças sem preenchimento, traço preto (estilo prancha técnica)
    technicalStyle: { type: Boolean, default: false },
    darkTheme: { type: Boolean, default: false },
    // COPILOT das bordas: { label } — a sugestão-fantasma é ABERTA (qualquer
    // peça de pedra pode receber peça secundária OU acabamento; o menu
    // combinado decide). label vazio = desativado.
    ghostConfig: {
      type: Object,
      default: () => ({ label: "" })
    }
  },
  data() {
    return {
      // estado de arraste/redimensionamento (não persiste, só durante o gesto)
      drag: null,
      // rascunho da PAREDE em desenho (ferramenta "utility:wall"): pontos já
      // colocados + ponto do mouse (rubber-band). Só existe enquanto a
      // ferramenta está ativa; nada é persistido até finalizar.
      wallDraft: null
    };
  },
  watch: {
    // trocou de ferramenta sem terminar a parede: descarta o rascunho
    activeTool(next) {
      if (next !== "utility:wall") this.wallDraft = null;
    }
  },
  mounted() {
    // estilos de render .std-* vêm de módulo único (compartilhado com a impressão)
    installStdStyles();
  },
  computed: {
    renderablePieces() {
      return getRenderablePieces(this.pieces);
    },
    contentTransform() {
      return `translate(${this.panX} ${this.panY}) scale(${this.zoom})`;
    },
    boundsCtx() {
      return { scale: this.scale, width: this.width, height: this.height };
    },
    // pré-calcula tudo de cada peça (bounds, fill, bordas, cotas, rótulos),
    // evitando recomputar nos múltiplos pontos do template.
    decoratedPieces() {
      // bounds de todas as peças primeiro: as cotas desviam de vizinhos
      const pieces = this.renderablePieces;
      const allBounds = pieces.map(p => getPieceBounds(p, this.boundsCtx));

      // 1ª passada: peças SEM vínculo (principais) — régua por colisão/manual
      const dimSidesByIndex = pieces.map((p, i) =>
        p.linkedTo && p.linkedTo.edge
          ? null // resolvida na 2ª passada (depende do lado usado pelo pai)
          : this.dimSidesForPiece(p, allBounds[i], allBounds, i)
      );
      // 2ª passada: peças VINCULADAS (saia/rodapé) — régua sempre para FORA
      // (nunca entre as duas peças) e oculta a régua que ficaria paralela e
      // duplicada à do pai no mesmo lado
      pieces.forEach((p, i) => {
        if (dimSidesByIndex[i]) return;
        const parentIndex = pieces.findIndex(
          q => p.linkedTo && q.fichaId === p.linkedTo.parentId
        );
        dimSidesByIndex[i] = this.linkedDimSides(
          p,
          allBounds[i],
          parentIndex >= 0 ? pieces[parentIndex] : null,
          parentIndex >= 0 ? allBounds[parentIndex] : null,
          parentIndex >= 0 ? dimSidesByIndex[parentIndex] : null,
          allBounds,
          i
        );
      });

      return pieces.map((p, pieceIndex) => {
        const bounds = allBounds[pieceIndex];
        const dimSides = dimSidesByIndex[pieceIndex];
        const edges = renderAllEdges(p, bounds);
        const cutouts = (p.cutouts || []).map(c => {
          const px = cutoutToPx(c, p, bounds);
          const hitW = Math.max(px.w + 12 * this.textScale, 32 * this.textScale);
          const hitH = Math.max(px.h + 12 * this.textScale, 32 * this.textScale);
          return {
            cutout: c,
            px,
            hit: {
              x: px.cx - hitW / 2,
              y: px.cy - hitH / 2,
              w: hitW,
              h: hitH
            },
            primitives: getCutoutSymbol(c.kind, px),
            center: getCenterLines(px.cx, px.cy, Math.max(px.w, px.h) / 2, 6),
            sizeLabel: getCutoutSizeLabel(c),
            axisDims: getCutoutAxisDimensions(c, p, bounds)
          };
        });
        return {
          piece: p,
          bounds,
          fill: this.pieceFill(p),
          edges,
          activeEdges: edges.filter(e => e.active),
          dimSides,
          dims: getDimensionLines(p, bounds, dimSides),
          cutouts,
          chainDims: getChainDimensions(p.cutouts, p, bounds),
          outline: getPieceOutline(p, bounds),
          cornerPath: getPieceCornerPath(p, bounds),
          shapeDims: getShapeDimensions(p, bounds),
          label: this.pieceLabel(p),
          sub: this.pieceSubLabel(p)
        };
      });
    },
    resizeGhost() {
      return this.drag && this.drag.mode === "resize-piece" && this.drag.moved
        ? this.drag.ghost
        : null;
    },
    eraserRect() {
      return this.drag && this.drag.mode === "erase" && this.drag.moved
        ? this.drag.rect
        : null;
    },
    // TAMANHO dos textos/alças vs zoom: acima de 100% mantêm tamanho de
    // TELA constante (1/zoom) — nem crescem junto com a peça, nem somem.
    textScale() {
      if (this.zoom <= 1) return 1;
      return 1 / this.zoom;
    },
    // tamanhos efetivos aplicados INLINE em cada texto (não depende do
    // suporte a var() — o CSS compartilhado é só fallback p/ impressão)
    fontSizes() {
      const s = this.textScale;
      const px = base => ({ fontSize: `${(base * s).toFixed(2)}px` });
      return {
        dim: px(9),
        pieceLabel: px(11),
        pieceSub: px(9),
        elementTag: px(9),
        elementText: px(12),
        cutoutLabel: px(8.5),
        ghostLabel: px(8.5),
        ghostDim: px(10),
        swatchName: px(7.5)
      };
    },
    // medidas em metros do ghost, derivadas do DELTA de px aplicado à medida
    // real (evita distorção da espessura visual mínima de peças finas)
    resizeGhostMeters() {
      if (!this.resizeGhost) return null;
      const { origBounds, origMeters, ghost } = this.drag;
      const dw = pxToMeters(ghost.w - origBounds.w, this.scale);
      const dh = pxToMeters(ghost.h - origBounds.h, this.scale);
      return {
        w: Math.max(MIN_PIECE_METERS, snapMeters(origMeters.w + dw)),
        h: Math.max(MIN_PIECE_METERS, snapMeters(origMeters.h + dh))
      };
    },
    resizeGhostLabels() {
      const m = this.resizeGhostMeters;
      if (!m) return { w: "", h: "" };
      return { w: `${formatMeasure(m.w)}m`, h: `${formatMeasure(m.h)}m` };
    }
  },
  methods: {
    // ---- decoração de elementos circulares (eixo + cota Ø) ----
    circleDecor(el) {
      const cx = el.x + el.width / 2;
      const cy = el.y + el.height / 2;
      const r = Math.max(el.width, el.height) / 2;
      return {
        cx,
        cy,
        r,
        center: getCenterLines(cx, cy, r),
        dia: getDiameterCallout(cx, cy, r, Math.max(el.width, el.height), this.scale)
      };
    },
    // largura aproximada do fundo branco atrás do texto da cota
    // (acompanha o teto de tamanho do texto no zoom)
    dimTextWidth(text) {
      return Math.max(18, `${text || ""}`.length * 6.2) * this.textScale;
    },
    // ---- helpers de render (memo simples por chamada) ----
    boundsOf(piece) {
      return getPieceBounds(piece, this.boundsCtx);
    },
    edgesOf(piece) {
      return renderAllEdges(piece, this.boundsOf(piece));
    },
    dimsOf(piece) {
      return getDimensionLines(piece, this.boundsOf(piece));
    },
    pieceFill(piece) {
      if (this.darkTheme) {
        if (this.technicalStyle) return "#202c33";
        return DARK_ROLE_FILL[piece.visualRole] || DARK_ROLE_FILL.generic;
      }
      if (this.technicalStyle) return "#ffffff";
      return ROLE_FILL[piece.visualRole] || ROLE_FILL.generic;
    },
    pieceStroke(dp) {
      if (this.isSelected("piece", dp.piece.id)) return "#25d366";
      return this.darkTheme ? "#aebac1" : "#173b33";
    },
    pieceLabel(piece) {
      const prefix = piece.isPrimary ? "P" : "S";
      return `${prefix}${piece.sourceIndex + 1} - ${piece.description || "Peça"}`;
    },
    // como desenhar o rótulo conforme a ALTURA VISUAL da peça:
    // >= 34px: duas linhas dentro | >= 15px: uma linha dentro | menor: fora
    pieceLabelLayout(dp) {
      const h = dp.bounds.h;
      const needed = 24 * this.textScale; // duas linhas de texto
      if (h >= needed + 10) return "stacked";
      if (h >= 13 * this.textScale) return "inline";
      return "outside";
    },
    // sem preço no desenho técnico: só medidas e unidade
    pieceSubLabel(piece) {
      const unit = piece.billingUnit === "M2" ? "M²" : piece.billingUnit;
      const surface = piece.surfaceFinishItem && piece.surfaceFinishItem.nome;
      return `${formatMeasure(piece.width)}m x ${formatMeasure(piece.height)}m | ${unit}${
        surface ? ` | Superfície: ${surface}` : ""
      }`;
    },
    labelTextTransform(dp, x, y) {
      const angle = Number(dp && dp.piece && dp.piece.labelTextDirection) || 0;
      return angle ? `rotate(${angle} ${x} ${y})` : "";
    },
    annotationWidth(ann) {
      return (
        Math.max(40, `${ann.text || ""}`.length * (ann.fontSize || 12) * 0.6) *
        this.textScale
      );
    },
    isSelected(kind, id) {
      return this.selection && this.selection.kind === kind && this.selection.id === id;
    },

    // ---- conversão de coordenadas de tela -> coordenadas do desenho ----
    clientToContent(evt) {
      const svg = this.$refs.svg;
      const content = this.$refs.content;
      if (!svg || !content) return { x: 0, y: 0 };
      const pt = svg.createSVGPoint();
      pt.x = evt.clientX;
      pt.y = evt.clientY;
      const ctm = content.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const local = pt.matrixTransform(ctm.inverse());
      return { x: local.x, y: local.y };
    },
    // coords do viewBox do SVG (ANTES do zoom/pan do conteúdo) — usadas por
    // pan e zoom, que operam no espaço do viewport e não no do desenho.
    clientToSvg(evt) {
      const svg = this.$refs.svg;
      if (!svg) return { x: 0, y: 0 };
      const pt = svg.createSVGPoint();
      pt.x = evt.clientX;
      pt.y = evt.clientY;
      const ctm = svg.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const local = pt.matrixTransform(ctm.inverse());
      return { x: local.x, y: local.y };
    },

    // ---- zoom pela roda do mouse, focado no cursor ----
    onWheel(evt) {
      const point = this.clientToSvg(evt);
      this.$emit("wheel-zoom", {
        factor: evt.deltaY < 0 ? 1.12 : 1 / 1.12,
        x: point.x,
        y: point.y
      });
    },

    // ---- recortes vinculados ----
    // quais cotas automáticas desenhar: 1 recorte = eixo->bordas;
    // 2+ recortes = cota encadeada + eixos apenas do recorte selecionado
    cutoutDims(dp) {
      const cuts = dp.cutouts || [];
      let dims = [];
      if (cuts.length === 1) {
        dims = [cuts[0].axisDims.horizontal, cuts[0].axisDims.vertical];
      } else if (cuts.length > 1) {
        dims = [...dp.chainDims];
        const selected = cuts.find(c => this.isSelected("cutout", c.cutout.id));
        if (selected) {
          dims.push(selected.axisDims.horizontal, selected.axisDims.vertical);
        }
      }
      // cotas do recorte da silhueta (L/U)
      return dims.concat(dp.shapeDims || []);
    },
    findCutoutPiece(cutoutId) {
      return this.decoratedPieces.find(d =>
        (d.cutouts || []).some(c => c.cutout.id === cutoutId)
      );
    },
    // redimensionar recorte (cuba/cooktop) pela alça do canto inferior direito
    startCutoutResize(evt, cutoutId, rotation = 0) {
      if (this.readonly) return;
      this.drag = {
        mode: "resize-cutout",
        id: cutoutId,
        rotation,
        start: this.clientToContent(evt),
        moved: false
      };
    },
    emitSwatchSelect(dp) {
      if (!dp || !dp.piece) return;
      this.$emit("select", { kind: "piece", id: dp.piece.id });
    },
    // chamadas de borda (edgeLabels) posicionadas fora da peça
    edgeLabelItems(dp) {
      const labels = dp.piece.edgeLabels || {};
      const b = dp.bounds;
      const items = [];
      if (labels.front) {
        items.push({ edge: "front", text: labels.front, x: b.cx, y: b.y + b.h + 38, rotate: 0 });
      }
      if (labels.back) {
        items.push({ edge: "back", text: labels.back, x: b.cx, y: b.y - 28, rotate: 0 });
      }
      if (labels.left) {
        items.push({ edge: "left", text: labels.left, x: b.x - 28, y: b.cy, rotate: -90 });
      }
      if (labels.right) {
        items.push({ edge: "right", text: labels.right, x: b.x + b.w + 38, y: b.cy, rotate: -90 });
      }
      return items;
    },

    // ---- acabamento de borda direto no canvas ----
    // drop de um acabamento arrastado da paleta sobre a borda
    onEdgeDrop(dp, edge, evt) {
      const payload = evt.dataTransfer && evt.dataTransfer.getData("text/plain");
      if (!payload) return;
      this.$emit("edge-drop", {
        pieceId: dp.piece.id,
        edge: edge.edge,
        payload
      });
    },
    // Fallback de drop no corpo da peça: evita exigir que o atendente acerte
    // exatamente o traço da borda. A borda mais próxima do cursor é usada.
    onPieceDrop(dp, evt) {
      const payload = evt.dataTransfer && evt.dataTransfer.getData("text/plain");
      if (!payload || !`${payload}`.startsWith("finish:")) return;
      const point = this.clientToContent(evt);
      const b = dp.bounds;
      const angle = (-Number(dp.piece.rotation || 0) * Math.PI) / 180;
      const dx = point.x - b.cx;
      const dy = point.y - b.cy;
      const local = {
        x: b.cx + dx * Math.cos(angle) - dy * Math.sin(angle),
        y: b.cy + dx * Math.sin(angle) + dy * Math.cos(angle)
      };
      const distances = {
        back: Math.abs(local.y - b.y),
        front: Math.abs(local.y - (b.y + b.h)),
        left: Math.abs(local.x - b.x),
        right: Math.abs(local.x - (b.x + b.w))
      };
      const edge = Object.keys(distances).reduce((nearest, current) =>
        distances[current] < distances[nearest] ? current : nearest
      );
      this.$emit("edge-drop", {
        pieceId: dp.piece.id,
        edge,
        payload
      });
    },
    // swatches: imagem do acabamento cadastrado junto à borda equivalente.
    // `size` é salvo por borda (edgeFinishItems[edge].size), em UNIDADES DO
    // DESENHO — o swatch cresce com o zoom, como qualquer parte da peça.
    edgeSwatchItems(dp) {
      const items = dp.piece.edgeFinishItems || {};
      const b = dp.bounds;
      const result = [];
      ["front", "back", "left", "right"].forEach(edge => {
        const info = items[edge];
        if (!info || !info.nome) return;
        const size = Math.max(16, Number(info.size) || 28);
        const half = size / 2;
        const offset = 6 + half; // afastamento da borda em px do desenho
        const anchors = {
          front: {
            x: b.cx, y: b.y + b.h + offset,
            labelX: b.cx + half + 4, labelY: b.y + b.h + offset + 3, anchor: "start"
          },
          back: {
            x: b.cx, y: b.y - offset,
            labelX: b.cx + half + 4, labelY: b.y - offset + 3, anchor: "start"
          },
          left: {
            x: b.x - offset, y: b.cy,
            labelX: b.x - offset, labelY: b.cy + half + 9, anchor: "middle"
          },
          right: {
            x: b.x + b.w + offset, y: b.cy,
            labelX: b.x + b.w + offset, labelY: b.cy + half + 9, anchor: "middle"
          }
        };
        const swatch = {
          edge,
          href: info.imagem || "",
          nome: info.nome,
          size,
          half,
          ...anchors[edge]
        };
        const positioned = this.avoidDimCollision(swatch, edge, dp.dims, dp.dimSides);
        const offsetX = Number(info.offsetX) || 0;
        const offsetY = Number(info.offsetY) || 0;
        result.push({
          ...positioned,
          x: positioned.x + offsetX,
          y: positioned.y + offsetY,
          labelX: positioned.labelX + offsetX,
          labelY: positioned.labelY + offsetY,
          labelRotation: Number(info.labelRotation) || 0
        });
      });
      return result;
    },
    // desvia o swatch LATERALMENTE (nunca esconde o texto da medida) quando
    // ele cai em cima da cota de comprimento/largura da própria peça
    avoidDimCollision(swatch, edge, dims, dimSides) {
      if (!dims) return swatch;
      const horizontal = edge === "front" || edge === "back";
      const dim = horizontal ? dims.length : dims.width;
      const sides = dimSides || {};
      const matches = horizontal
        ? (edge === "front" ? (sides.lengthSide || "bottom") !== "top" : sides.lengthSide === "top")
        : (edge === "right" ? (sides.widthSide || "right") !== "left" : sides.widthSide === "left");
      if (!dim || !matches) return swatch;

      const scale = this.textScale;
      const label = dim.label;
      const textLen = this.dimTextWidth(label.text);
      // bbox aproximado do texto da cota (rotate -90 = etiqueta vertical)
      const labelRect = label.rotate
        ? { x: label.x - 6 * scale, y: label.y - textLen / 2, w: 12 * scale, h: textLen }
        : { x: label.x - textLen / 2, y: label.y - 9 * scale, w: textLen, h: 11 * scale };
      const swatchRect = {
        x: swatch.x - swatch.half - 1,
        y: swatch.y - swatch.half - 1,
        w: swatch.size + 2,
        h: swatch.size + 2
      };
      const overlap =
        swatchRect.x < labelRect.x + labelRect.w &&
        swatchRect.x + swatchRect.w > labelRect.x &&
        swatchRect.y < labelRect.y + labelRect.h &&
        swatchRect.y + swatchRect.h > labelRect.y;
      if (!overlap) return swatch;

      // move o SWATCH para o lado (a medida nunca sai do lugar)
      const shift = horizontal
        ? swatchRect.w / 2 + labelRect.w / 2 + 6
        : swatchRect.h / 2 + labelRect.h / 2 + 6;
      const moved = { ...swatch };
      if (horizontal) {
        moved.x += shift;
        moved.labelX += shift;
      } else {
        moved.y += shift;
        moved.labelY += shift;
      }
      return moved;
    },
    // redimensionar o swatch do acabamento pela alça do canto
    startSwatchResize(evt, pieceId, edge) {
      if (this.readonly) return;
      this.$emit("select", { kind: "piece", id: pieceId });
      this.drag = {
        mode: "resize-swatch",
        pieceId,
        edge,
        start: this.clientToContent(evt),
        moved: false
      };
    },
    startSwatchMove(evt, pieceId, edge, rotation = 0) {
      if (this.readonly || evt.button !== 0) return;
      this.$emit("select", { kind: "piece", id: pieceId });
      this.drag = {
        mode: "move-swatch",
        pieceId,
        edge,
        rotation,
        start: this.clientToContent(evt),
        moved: false
      };
    },
    // COPILOT: sugestões-fantasma nas bordas da peça de pedra SELECIONADA
    // (qualquer papel). Clique abre o menu combinado: Peça Secundária OU
    // Acabamento — a hierarquia da montagem fica em aberto.
    ghostStrips(dp) {
      const config = this.ghostConfig || {};
      const label = dp.piece.isStone ? config.label : "";
      if (
        this.readonly ||
        !label ||
        !dp.piece.isPrimary ||
        this.activeTool !== "select" ||
        !this.isSelected("piece", dp.piece.id)
      ) {
        return [];
      }
      const withLength = true;
      // só bordas já ocupadas por peça vinculada não recebem sugestão
      const parentId = dp.piece.fichaId;
      const childEdges = this.pieces
        .filter(p => p.linkedTo && parentId && p.linkedTo.parentId === parentId)
        .map(p => p.linkedTo.edge);
      const b = dp.bounds;
      const t = 12; // espessura visual da sombra (~10cm)
      const gap = 2;
      const defs = {
        front: {
          x: b.x, y: b.y + b.h + gap, w: b.w, h: t,
          labelX: b.cx, labelY: b.y + b.h + gap + t - 3, rotate: 0,
          len: dp.piece.width
        },
        back: {
          x: b.x, y: b.y - gap - t, w: b.w, h: t,
          labelX: b.cx, labelY: b.y - gap - t + 9, rotate: 0,
          len: dp.piece.width
        },
        left: {
          x: b.x - gap - t, y: b.y, w: t, h: b.h,
          labelX: b.x - gap - t / 2, labelY: b.cy, rotate: -90,
          len: dp.piece.height
        },
        right: {
          x: b.x + b.w + gap, y: b.y, w: t, h: b.h,
          labelX: b.x + b.w + gap + t / 2, labelY: b.cy, rotate: -90,
          len: dp.piece.height
        }
      };
      return ["front", "back", "left", "right"]
        .filter(edge => !childEdges.includes(edge))
        .map(edge => ({
          edge,
          ...defs[edge],
          label: withLength
            ? `+ ${label} ${formatMeasure(defs[edge].len)}m`
            : `+ ${label}`
        }));
    },
    emitEdgeClick(dp, edge, evt) {
      this.$emit("select", { kind: "piece", id: dp.piece.id });
      this.$emit("edge-click", {
        pieceId: dp.piece.id,
        edge: edge.edge,
        clientX: evt ? evt.clientX : 0,
        clientY: evt ? evt.clientY : 0
      });
    },
    emitEdgeMenu(dp, edge, evt) {
      this.$emit("select", { kind: "piece", id: dp.piece.id });
      this.$emit("edge-menu", {
        pieceId: dp.piece.id,
        edge: edge.edge,
        clientX: evt.clientX,
        clientY: evt.clientY
      });
    },
    emitPieceMenu(dp, evt) {
      if (this.readonly) return;
      this.$emit("select", { kind: "piece", id: dp.piece.id });
      this.$emit("piece-menu", {
        pieceId: dp.piece.id,
        clientX: evt.clientX,
        clientY: evt.clientY
      });
    },

    // ---- posicionamento automático das cotas ----
    // se outra peça ocupa a faixa onde a régua desenharia (balcão em L),
    // a cota flipa para o lado livre (cima/esquerda)
    emitCutoutMenu(cr, evt) {
      if (this.readonly) return;
      this.$emit("select", { kind: "cutout", id: cr.cutout.id });
      this.$emit("cutout-menu", {
        id: cr.cutout.id,
        clientX: evt.clientX,
        clientY: evt.clientY
      });
    },
    dimSidesForPiece(piece, b, allBounds, selfIndex) {
      const manual = piece.dimensionSides || {};
      const auto = this.autoDimSides(b, allBounds, selfIndex);
      return {
        lengthSide: manual.lengthSide || auto.lengthSide,
        widthSide: manual.widthSide || auto.widthSide
      };
    },
    /**
     * Régua de uma peça VINCULADA (saia/rodapé/frontão com linkedTo): por
     * padrão sempre para FORA da peça principal (nunca entra "para dentro"),
     * calculado geometricamente a partir da posição real do centro da peça
     * em relação ao pai (não depende de suposições sobre rotation/edge) —
     * projeta a direção padrão de cada régua (local bottom/right) pela
     * rotação da peça e escolhe o lado (ou o oposto) que aponta pra longe
     * do pai. Se o pai já mostra a régua correspondente do MESMO lado,
     * oculta a da peça vinculada (evita duas réguas paralelas/duplicadas).
     */
    linkedDimSides(piece, bounds, parent, parentBounds, parentSides, allBounds, selfIndex) {
      const manual = piece.dimensionSides || {};
      if (!parent || !parentBounds) {
        const auto = this.autoDimSides(bounds, allBounds, selfIndex);
        return {
          lengthSide: manual.lengthSide || auto.lengthSide,
          widthSide: manual.widthSide || auto.widthSide
        };
      }
      const rad = (((Number(piece.rotation) || 0) * Math.PI) / 180);
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      // direção em tela para onde um vetor LOCAL aponta após a rotação da
      // peça (mesma transformação `rotate()` aplicada ao grupo no SVG)
      const rotateDir = (x, y) => ({ x: x * cos - y * sin, y: x * sin + y * cos });
      const dx = bounds.cx - parentBounds.cx;
      const dy = bounds.cy - parentBounds.cy;

      let lengthSide = manual.lengthSide;
      if (!lengthSide) {
        const bottomDir = rotateDir(0, 1); // direção padrão da régua de comprimento
        lengthSide = bottomDir.x * dx + bottomDir.y * dy >= 0 ? "bottom" : "top";
        if (parentSides && parentSides.lengthSide === lengthSide) lengthSide = "off";
      }
      let widthSide = manual.widthSide;
      if (!widthSide) {
        const rightDir = rotateDir(1, 0); // direção padrão da régua de largura
        widthSide = rightDir.x * dx + rightDir.y * dy >= 0 ? "right" : "left";
        if (parentSides && parentSides.widthSide === widthSide) widthSide = "off";
      }
      return { lengthSide, widthSide };
    },
    autoDimSides(b, allBounds, selfIndex) {
      const band = 38; // offset da cota + texto
      const intersects = (r, o) =>
        !(
          o.x + o.w <= r.x ||
          o.x >= r.x + r.w ||
          o.y + o.h <= r.y ||
          o.y >= r.y + r.h
        );
      const others = allBounds.filter((o, i) => i !== selfIndex);
      const busy = region => others.some(o => intersects(region, o));
      const below = { x: b.x, y: b.y + b.h, w: b.w, h: band };
      const top = { x: b.x, y: b.y - band, w: b.w, h: band };
      const right = { x: b.x + b.w, y: b.y, w: band, h: b.h };
      const left = { x: b.x - band, y: b.y, w: band, h: b.h };
      return {
        lengthSide: busy(below) && !busy(top) ? "top" : "bottom",
        widthSide: busy(right) && !busy(left) ? "left" : "right"
      };
    },

    // ---- edição de cota (duplo clique) ----
    emitEditDimension(dp, axis, dim) {
      if (this.readonly || !dp.piece.isStone) return;
      this.$emit("edit-dimension", {
        pieceId: dp.piece.id,
        axis, // 'length' (comprimento) | 'width' (largura)
        x: dim.label.x,
        y: dim.label.y
      });
    },
    // converte coords do desenho -> coords de tela (posicionar overlays HTML)
    contentToClient(x, y) {
      const svg = this.$refs.svg;
      const content = this.$refs.content;
      if (!svg || !content) return { x: 0, y: 0 };
      const pt = svg.createSVGPoint();
      pt.x = x;
      pt.y = y;
      const ctm = content.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const p = pt.matrixTransform(ctm);
      return { x: p.x, y: p.y };
    },

    // ---- redimensionamento de peça por alças ----
    canResizePiece(dp) {
      return (
        !this.readonly &&
        this.activeTool === "select" &&
        dp.piece.isStone &&
        this.isSelected("piece", dp.piece.id)
      );
    },
    resizeHandles(b, rotation) {
      // o CURSOR precisa refletir a direção NA TELA: com a peça girada, a
      // alça "leste" aparece embaixo — a seta deve ser vertical, não <-->
      const cycle = ["ns-resize", "nesw-resize", "ew-resize", "nwse-resize"];
      const baseIndex = { n: 0, ne: 1, e: 2, se: 3, s: 0, sw: 1, w: 2, nw: 3 };
      const steps = Math.round((((rotation || 0) % 360) + 360) % 360 / 45);
      const cursorFor = handle => cycle[(baseIndex[handle] + steps) % 4];
      const mx = b.x + b.w / 2;
      const my = b.y + b.h / 2;
      return [
        { handle: "nw", x: b.x, y: b.y },
        { handle: "n", x: mx, y: b.y },
        { handle: "ne", x: b.x + b.w, y: b.y },
        { handle: "e", x: b.x + b.w, y: my },
        { handle: "se", x: b.x + b.w, y: b.y + b.h },
        { handle: "s", x: mx, y: b.y + b.h },
        { handle: "sw", x: b.x, y: b.y + b.h },
        { handle: "w", x: b.x, y: my }
      ].map(h => ({ ...h, cursor: cursorFor(h.handle) }));
    },
    // ponto do vértice interno da silhueta (alça de ajuste do recorte L/U).
    // Formatos da BORRACHA (custom) são editados pela própria borracha.
    shapeHandlePoint(dp) {
      if (!dp.outline || dp.outline.kind === "custom") return null;
      const W = dp.piece.width;
      const H = dp.piece.height && dp.piece.height > 0.1 ? dp.piece.height : 0.1;
      const eps = 0.0001;
      const interior = dp.outline.points.filter(
        p =>
          p.mx > eps && p.mx < W - eps && p.my > eps && p.my < H - eps
      );
      return interior.length ? interior[interior.length - 1] : null;
    },
    startShapeResize(evt, pieceId) {
      if (this.readonly) return;
      this.drag = {
        mode: "shape-handle",
        id: pieceId,
        start: this.clientToContent(evt),
        moved: false
      };
    },
    startPieceResize(evt, pieceId, handle) {
      if (this.readonly) return;
      const dp = this.decoratedPieces.find(d => d.piece.id === pieceId);
      if (!dp) return;
      this.drag = {
        mode: "resize-piece",
        id: pieceId,
        handle,
        rotation: dp.piece.rotation || 0,
        startContent: this.clientToContent(evt),
        origBounds: { ...dp.bounds },
        origMeters: { w: dp.piece.width, h: dp.piece.height },
        ghost: { x: dp.bounds.x, y: dp.bounds.y, w: dp.bounds.w, h: dp.bounds.h },
        moved: false
      };
    },
    updatePieceResizeGhost(evt) {
      const cur = this.clientToContent(evt);
      let dx = cur.x - this.drag.startContent.x;
      let dy = cur.y - this.drag.startContent.y;
      if (!this.drag.moved && Math.abs(dx) + Math.abs(dy) < 2) return;
      this.drag.moved = true;

      // peça GIRADA: converte o arrasto (tela) para o eixo LOCAL da peça —
      // numa saia vertical, puxar para baixo aumenta o comprimento, como o
      // gesto sugere (sem isso o arrasto ficava "trocado")
      const rot = ((this.drag.rotation || 0) * Math.PI) / 180;
      if (rot) {
        const cos = Math.cos(rot);
        const sin = Math.sin(rot);
        const localDx = dx * cos + dy * sin;
        const localDy = -dx * sin + dy * cos;
        dx = localDx;
        dy = localDy;
      }

      const o = this.drag.origBounds;
      const h = this.drag.handle;
      const g = { x: o.x, y: o.y, w: o.w, h: o.h };
      const minPx = 4;
      if (h.includes("e")) g.w = Math.max(minPx, o.w + dx);
      if (h.includes("w")) {
        g.w = Math.max(minPx, o.w - dx);
        g.x = o.x + o.w - g.w;
      }
      if (h.includes("s")) g.h = Math.max(minPx, o.h + dy);
      if (h.includes("n")) {
        g.h = Math.max(minPx, o.h - dy);
        g.y = o.y + o.h - g.h;
      }
      this.drag.ghost = g;
    },

    // ---- borracha: arrasta um retângulo para apagar parte da peça ----
    startEraseDrag(evt) {
      const start = this.clientToContent(evt);
      this.drag = {
        mode: "erase",
        origin: start,
        rect: { x: start.x, y: start.y, w: 0, h: 0 },
        moved: false
      };
    },
    updateEraseDrag(evt) {
      const cur = this.clientToContent(evt);
      const o = this.drag.origin;
      const rect = {
        x: Math.min(o.x, cur.x),
        y: Math.min(o.y, cur.y),
        w: Math.abs(cur.x - o.x),
        h: Math.abs(cur.y - o.y)
      };
      if (!this.drag.moved && rect.w + rect.h < 3) return;
      this.drag.moved = true;
      this.drag.rect = rect;
    },

    // ---- arraste ----
    startDrag(evt, kind, id, rotation = 0) {
      if (evt.button === 2) return;
      // borracha ativa: qualquer mousedown (mesmo sobre entidades) apaga
      if (!this.readonly && this.activeTool === "eraser") {
        this.startEraseDrag(evt);
        return;
      }
      // o líder pertence à anotação selecionada: arrastar a ponta não muda a seleção
      if (kind !== "leader") this.$emit("select", { kind, id });
      if (this.readonly) return;
      if (this.activeTool && this.activeTool !== "select" && this.activeTool !== "move") {
        // em modo de ferramenta de inserção, clique seleciona mas não arrasta
        return;
      }
      const start = this.clientToContent(evt);
      this.drag = { mode: "move", kind, id, rotation, start, moved: false };
    },
    startResize(evt, kind, id) {
      if (this.readonly) return;
      const start = this.clientToContent(evt);
      this.drag = { mode: "resize", kind, id, start, moved: false };
    },
    onMouseMove(evt) {
      // rascunho da PAREDE: atualiza o "rubber-band" até o cursor (não
      // depende de this.drag — não há arraste em andamento entre cliques)
      if (this.wallDraft) {
        this.wallDraft.previewPoint = this.clientToContent(evt);
      }
      if (!this.drag) return;

      // ponto-âncora da parede: arrasto contínuo (sem ghost)
      if (this.drag.mode === "wall-point") {
        const cur = this.clientToContent(evt);
        const dx = cur.x - this.drag.start.x;
        const dy = cur.y - this.drag.start.y;
        this.drag.start = cur;
        this.$emit("move-wall-point", { id: this.drag.elId, pointIndex: this.drag.pointIndex, dx, dy });
        return;
      }
      // alça de curvatura (bow) de um segmento da parede: arrasto contínuo
      if (this.drag.mode === "wall-bow") {
        const cur = this.clientToContent(evt);
        const dx = cur.x - this.drag.start.x;
        const dy = cur.y - this.drag.start.y;
        this.drag.start = cur;
        this.$emit("move-wall-bow", { id: this.drag.elId, segIndex: this.drag.segIndex, dx, dy });
        return;
      }

      // borracha: atualiza o retângulo; o corte acontece no mouseup
      if (this.drag.mode === "erase") {
        this.updateEraseDrag(evt);
        return;
      }

      // resize de peça: só atualiza o ghost; o commit acontece no mouseup
      if (this.drag.mode === "resize-piece") {
        this.updatePieceResizeGhost(evt);
        return;
      }

      // alça do swatch de acabamento: delta direto em px do desenho
      // (o swatch vive nas unidades do desenho, sem contra-escala)
      if (this.drag.mode === "resize-swatch") {
        const cur = this.clientToContent(evt);
        const dx = cur.x - this.drag.start.x;
        const dy = cur.y - this.drag.start.y;
        if (!this.drag.moved && Math.abs(dx) + Math.abs(dy) < 1) return;
        this.drag.moved = true;
        this.drag.start = cur;
        this.$emit("resize-swatch", {
          pieceId: this.drag.pieceId,
          edge: this.drag.edge,
          delta: (dx + dy) / 2
        });
        return;
      }

      if (this.drag.mode === "move-swatch") {
        const cur = this.clientToContent(evt);
        const dx = cur.x - this.drag.start.x;
        const dy = cur.y - this.drag.start.y;
        if (!this.drag.moved && Math.abs(dx) + Math.abs(dy) < 1) return;
        this.drag.moved = true;
        this.drag.start = cur;
        const localDelta = globalDeltaToPieceLocal(
          dx,
          dy,
          this.drag.rotation
        );
        this.$emit("move-swatch", {
          pieceId: this.drag.pieceId,
          edge: this.drag.edge,
          dx: localDelta.dx,
          dy: localDelta.dy
        });
        return;
      }

      // alça do recorte (cuba/cooktop): deltas em METROS relativos à peça
      if (this.drag.mode === "resize-cutout") {
        const cur = this.clientToContent(evt);
        const globalDx = cur.x - this.drag.start.x;
        const globalDy = cur.y - this.drag.start.y;
        const { dx, dy } = globalDeltaToPieceLocal(
          globalDx,
          globalDy,
          this.drag.rotation
        );
        if (!this.drag.moved && Math.abs(dx) + Math.abs(dy) < 1) return;
        this.drag.moved = true;
        this.drag.start = cur;
        const dp = this.findCutoutPiece(this.drag.id);
        if (dp) {
          const visualH =
            dp.piece.height && dp.piece.height > 0.1 ? dp.piece.height : 0.1;
          this.$emit("resize-cutout", {
            id: this.drag.id,
            dwM: (dx * (dp.piece.width || 1)) / dp.bounds.w,
            dhM: (dy * visualH) / dp.bounds.h
          });
        }
        return;
      }

      // alça da silhueta L/U: deltas em METROS relativos à peça
      if (this.drag.mode === "shape-handle") {
        const cur = this.clientToContent(evt);
        const dx = cur.x - this.drag.start.x;
        const dy = cur.y - this.drag.start.y;
        if (!this.drag.moved && Math.abs(dx) + Math.abs(dy) < 1) return;
        this.drag.moved = true;
        this.drag.start = cur;
        const dp = this.decoratedPieces.find(d => d.piece.id === this.drag.id);
        if (dp) {
          const visualH =
            dp.piece.height && dp.piece.height > 0.1 ? dp.piece.height : 0.1;
          this.$emit("shape-resize", {
            id: this.drag.id,
            dxM: (dx * (dp.piece.width || 1)) / dp.bounds.w,
            dyM: (dy * visualH) / dp.bounds.h
          });
        }
        return;
      }

      // pan do viewport: deltas no espaço do SVG (fora do transform de conteúdo)
      if (this.drag.mode === "pan") {
        const curSvg = this.clientToSvg(evt);
        const dxs = curSvg.x - this.drag.startSvg.x;
        const dys = curSvg.y - this.drag.startSvg.y;
        if (!this.drag.moved && Math.abs(dxs) + Math.abs(dys) < 2) return;
        this.drag.moved = true;
        this.drag.startSvg = curSvg;
        this.$emit("pan-by", { dx: dxs, dy: dys });
        return;
      }

      const cur = this.clientToContent(evt);
      const dx = cur.x - this.drag.start.x;
      const dy = cur.y - this.drag.start.y;
      if (!this.drag.moved && Math.abs(dx) + Math.abs(dy) < 1) return;
      this.drag.moved = true;
      this.drag.start = cur;

      if (this.drag.mode === "resize") {
        this.$emit("resize-entity", { kind: this.drag.kind, id: this.drag.id, dx, dy });
        return;
      }

      if (this.drag.kind === "piece") {
        // peça: converter delta px -> delta percentual do canvas
        this.$emit("move-piece", {
          id: this.drag.id,
          dxPct: (dx / this.width) * 100,
          dyPct: (dy / this.height) * 100
        });
      } else if (this.drag.kind === "leader") {
        // ponta do líder de uma anotação: delta em px do desenho
        this.$emit("move-leader", { id: this.drag.id, dx, dy });
      } else if (this.drag.kind === "cutout") {
        // recorte: delta px -> delta em METROS relativos à peça dona
        const dp = this.findCutoutPiece(this.drag.id);
        if (dp) {
          const localDelta = globalDeltaToPieceLocal(dx, dy, this.drag.rotation);
          const visualH =
            dp.piece.height && dp.piece.height > 0.1 ? dp.piece.height : 0.1;
          this.$emit("move-cutout", {
            pieceId: dp.piece.id,
            id: this.drag.id,
            dxM: (localDelta.dx * (dp.piece.width || 1)) / dp.bounds.w,
            dyM: (localDelta.dy * visualH) / dp.bounds.h
          });
        }
      } else {
        // elemento / anotação / imagem: delta em px do desenho
        this.$emit("move-entity", { kind: this.drag.kind, id: this.drag.id, dx, dy });
      }
    },
    endDrag() {
      // clique seco no vazio (pan que não se moveu) = limpar seleção
      if (this.drag && this.drag.mode === "pan" && !this.drag.moved && this.drag.clearOnClick) {
        this.$emit("select", { kind: null, id: null });
      }
      // borracha: solta = corta a área desenhada da peça sob o retângulo
      if (this.drag && this.drag.mode === "erase" && this.drag.moved) {
        this.$emit("erase-rect", { ...this.drag.rect });
      }
      // commit do resize de peça: emite medidas snapadas + novo centro.
      // Em peças giradas o ghost vive no frame LOCAL: o deslocamento do
      // centro precisa ser rotacionado de volta para o canvas.
      if (this.drag && this.drag.mode === "resize-piece" && this.drag.moved) {
        const meters = this.resizeGhostMeters;
        const g = this.drag.ghost;
        if (meters) {
          const o = this.drag.origBounds;
          let cx = g.x + g.w / 2;
          let cy = g.y + g.h / 2;
          const rot = ((this.drag.rotation || 0) * Math.PI) / 180;
          if (rot) {
            const ocx = o.x + o.w / 2;
            const ocy = o.y + o.h / 2;
            const ldx = cx - ocx;
            const ldy = cy - ocy;
            const cos = Math.cos(rot);
            const sin = Math.sin(rot);
            cx = ocx + ldx * cos - ldy * sin;
            cy = ocy + ldx * sin + ldy * cos;
          }
          this.$emit("resize-piece", {
            id: this.drag.id,
            width: meters.w,
            height: meters.h,
            position: {
              x: Math.max(0, Math.min(100, (cx / this.width) * 100)),
              y: Math.max(0, Math.min(100, (cy / this.height) * 100))
            }
          });
        }
      }
      this.drag = null;
    },

    // ---- clique no vazio: insere com a ferramenta ativa, pan, ou limpa seleção ----
    // (cliques em entidades usam @mousedown.stop, então não chegam aqui)
    onCanvasMouseDown(evt) {
      if (this.drag) return;
      // botão do meio: sempre pan
      if (evt.button === 1) {
        this.drag = { mode: "pan", startSvg: this.clientToSvg(evt), moved: false, clearOnClick: false };
        return;
      }
      if (evt.button !== 0) return;
      if (!this.readonly && this.activeTool === "eraser") {
        this.startEraseDrag(evt);
        return;
      }
      // PAREDE (utility:wall): cada clique acrescenta um ponto ao rascunho;
      // duplo clique/Enter finaliza (ver finishWallDraft), Esc cancela.
      if (!this.readonly && this.activeTool === "utility:wall") {
        this.addWallDraftPoint(evt);
        return;
      }
      const point = this.clientToContent(evt);
      if (this.activeTool && this.activeTool !== "select" && this.activeTool !== "move") {
        this.$emit("canvas-insert", { tool: this.activeTool, x: point.x, y: point.y });
        return;
      }
      // arrastar o fundo = pan; soltar sem mover = limpar seleção
      this.drag = { mode: "pan", startSvg: this.clientToSvg(evt), moved: false, clearOnClick: true };
    },

    // ---- PAREDE: rascunho multi-ponto em bezier (menu Utilidades) ----
    addWallDraftPoint(evt) {
      const point = this.clientToContent(evt);
      if (!this.wallDraft) this.wallDraft = { points: [], previewPoint: null };
      this.wallDraft.points.push({ x: point.x, y: point.y });
    },
    onCanvasDblClick() {
      if (this.activeTool === "utility:wall") this.finishWallDraft();
    },
    // finaliza o rascunho (chamado por dblclick ou Enter, via $refs do pai).
    // Remove pontos quase-coincidentes (o próprio duplo clique gera 2 no
    // mesmo lugar) antes de decidir se há parede o bastante para criar.
    finishWallDraft() {
      if (!this.wallDraft) return;
      const cleaned = [];
      this.wallDraft.points.forEach(p => {
        const last = cleaned[cleaned.length - 1];
        if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 4) cleaned.push(p);
      });
      this.wallDraft = null;
      if (cleaned.length >= 2) this.$emit("create-wall", cleaned);
      this.$emit("wall-draft-ended");
    },
    cancelWallDraft() {
      this.wallDraft = null;
    },

    // ---- PAREDE: edição depois de criada (alças de âncora e de curvatura) ----
    wallPathD(el) {
      return getWallPathD(el.points, el.bows);
    },
    wallHandles(el) {
      return getWallHandles(el.points, el.bows);
    },
    // borda da faixa: mesma cor de seleção usada nas peças (verde), senão
    // o cinza padrão — igual ao pieceStroke().
    wallOutlineColor(el) {
      if (this.isSelected("element", el.id)) return "#25d366";
      return this.darkTheme ? "#c9765a" : "#7a3c22";
    },
    // espessura real de alvenaria (15cm) convertida p/ px de conteúdo —
    // some/aumenta com o zoom igual a qualquer peça, não é alça de UI.
    wallThicknessPx() {
      return metersToPx(WALL_THICKNESS_M, this.scale);
    },
    // dimensões do ladrilho da textura de tijolo: 2 fiadas por tile (a de
    // baixo desloca meio tijolo — "amarração"), altura total = espessura
    // da parede, largura proporcional (tijolo ~2.2:1).
    brickTile() {
      const h = Math.max(6, this.wallThicknessPx() / 2);
      return { w: h * 2.2, h: h * 2 };
    },
    // ângulos SEMPRE visíveis em cada quina (não depende de seleção/hover);
    // a etiqueta some ~20px de conteúdo pra dentro do ângulo (bissetriz),
    // com contra-escala pra não crescer/encolher com o zoom.
    wallAngles(el) {
      return computeWallAngles(el.points).map(a => ({
        ...a,
        labelX: a.x + a.dirX * 20 * this.textScale,
        labelY: a.y + a.dirY * 20 * this.textScale
      }));
    },
    // rascunho em andamento: inclui o ponto sob o cursor (se houver) como
    // vértice provisório — mostra a faixa/tijolos/ângulo em tempo real
    // antes mesmo do clique confirmar o próximo ponto.
    wallDraftPreviewPoints() {
      if (!this.wallDraft) return [];
      return this.wallDraft.previewPoint
        ? [...this.wallDraft.points, this.wallDraft.previewPoint]
        : this.wallDraft.points;
    },
    wallDraftAngles() {
      return computeWallAngles(this.wallDraftPreviewPoints()).map(a => ({
        ...a,
        labelX: a.x + a.dirX * 20 * this.textScale,
        labelY: a.y + a.dirY * 20 * this.textScale
      }));
    },
    startWallPointDrag(evt, elId, pointIndex) {
      if (this.readonly) return;
      this.$emit("select", { kind: "element", id: elId });
      this.drag = {
        mode: "wall-point",
        elId,
        pointIndex,
        start: this.clientToContent(evt)
      };
    },
    startWallBowDrag(evt, elId, segIndex) {
      if (this.readonly) return;
      this.$emit("select", { kind: "element", id: elId });
      this.drag = {
        mode: "wall-bow",
        elId,
        segIndex,
        start: this.clientToContent(evt)
      };
    }
  }
};
</script>

<style scoped>
/* Apenas LAYOUT e INTERAÇÃO aqui. As classes de RENDER (.std-ext, .std-dim*,
   .std-piece-label, etc.) moram em engine/print/stdCanvasStyles.js — fonte
   única compartilhada com a janela de impressão. */
.std-canvas {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 14px;
  border: 1px solid rgba(7, 94, 84, 0.16);
  background: #eef3f0;
  user-select: none;
  touch-action: none;
}
.std-canvas--dark {
  background: #0b141a;
  border-color: #26343c;
}
.std-canvas--dark ::v-deep .std-piece-label,
.std-canvas--dark ::v-deep .std-piece-sub,
.std-canvas--dark ::v-deep .std-dim-text,
.std-canvas--dark ::v-deep .std-cutout-label,
.std-canvas--dark ::v-deep .std-element-tag,
.std-canvas--dark ::v-deep .std-element-text,
.std-canvas--dark ::v-deep .std-annotation-text,
.std-canvas--dark ::v-deep .std-edge-swatch-name,
.std-canvas--dark ::v-deep .std-ghost-suggestion-label,
.std-canvas--dark ::v-deep .std-ghost-dim,
.std-canvas--dark ::v-deep .std-wall-angle-text {
  fill: #e9edef;
}
.std-canvas--dark ::v-deep .std-dim-bg {
  fill: #0b141a;
  opacity: 0.92;
}
.std-canvas--dark ::v-deep .std-ext,
.std-canvas--dark ::v-deep .std-dimline,
.std-canvas--dark ::v-deep .std-centerline,
.std-canvas--dark ::v-deep .std-cutout-outline,
.std-canvas--dark ::v-deep .std-cutout-detail,
.std-canvas--dark ::v-deep .std-edge-decor,
.std-canvas--dark ::v-deep .std-leader-line {
  stroke: #aebac1;
}
.std-canvas--dark ::v-deep .std-cutout-outline,
.std-canvas--dark ::v-deep .std-edge-swatch-frame {
  fill: #111b21;
  stroke: #aebac1;
}
.std-canvas--dark ::v-deep .std-ghost-suggestion {
  fill: rgba(37, 211, 102, 0.08);
  stroke: rgba(37, 211, 102, 0.55);
}
.std-canvas--dark ::v-deep .std-wall-angle-bg {
  fill: #0b141a;
  opacity: 0.92;
}
.std-canvas--dark ::v-deep .std-wall-anchor,
.std-canvas--dark ::v-deep .std-wall-bow {
  fill: #111b21;
}
.std-canvas--tool {
  cursor: crosshair;
}
.std-piece,
.std-element,
.std-annotation,
.std-image,
.std-cutout {
  cursor: move;
}
.std-cutout-hit {
  pointer-events: all;
}
.std-cutout:active {
  cursor: grabbing;
}
.std-cutout-label {
  pointer-events: none;
}
.std-piece-label,
.std-piece-sub,
.std-annotation-text {
  pointer-events: none;
}
.std-handle {
  cursor: nwse-resize;
}
.std-wall-anchor {
  cursor: move;
}
.std-wall-bow {
  cursor: ns-resize;
}
.std-dim-editable {
  cursor: text;
}
.std-edge-hit {
  cursor: pointer;
  pointer-events: stroke;
}
.std-ghost {
  cursor: pointer;
}
.std-ghost:hover .std-ghost-suggestion {
  fill: rgba(0, 0, 0, 0.08);
  stroke: rgba(23, 59, 51, 0.8);
}
</style>
